import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { Skeleton } from '@meitu/saas-ui';
import { OpenPluginType } from '@meitu/saas-editor-core/es/version-types';
import { CollapseType } from '@meitu/saas-editor-ui/es/types';
import {
    CLOSE_IMAGE_TRANS_EVENT,
    events,
    MENU_SCROLL_EVENT,
    MORE_COLLAPSE_EVENT,
    PLUGIN_OPEN_EVENT,
} from '@/events-bus';
import { useDisableAppData, useFeatureHelper, useLockAppData } from '@/hooks';
import { editor } from '@/instance';
import { isIntl } from '@/react-process-env';
import { useStoreActions, useStoreState } from '@/store';
import { checkNeedConfirm } from '@/utils/editor';
import { type IMenuItem } from './config';
import { useMenu } from './use-menu';
import './index.less';
import { QuickStageOperate } from '@meitu/saas-editor-core/es/plugins/stage-operate';
import IconFont from '@meitu/xdesign-icon-font';

const useWasmIntState = () => {
    const [wasmInitState, setWasmInitState] = useState<boolean>(false);
    useEffect(() => {
        setWasmInitState(editor.wasm.getIsReady());
        editor.wasm.onWasmReady(() => {
            setWasmInitState(true);
        });
    }, []);
    return wasmInitState;
};

const MenuItem: React.FC<{
    data: IMenuItem;
    isWasmInt: boolean;
}> = React.memo(({ data, isWasmInt }) => {
    const featureHelper = useFeatureHelper();
    const lockApp = useLockAppData();
    const disableApp = useDisableAppData();

    const currentType = useStoreState((state) => state.collapseModel.currentType);
    const setCurrentType = useStoreActions((actions) => actions.collapseModel.setCurrentType);
    const isDisableAppAndShowPanel = useStoreState((state) => state.editorModel.isDisableAppAndShowPanel);
    const setActualType = useStoreActions((actions) => actions.collapseModel.setActualType);

    const closeAiPPTPrompt = useStoreActions((actions) => actions.navigationCollapseModel.closeAiPPTPrompt);
    const getRecordInfo = useStoreActions((actions) => actions.recordModel.getRecordInfo);
    const userInfo = useStoreState((state) => state.userInfoModel.userInfo);
    const isShowCollapse = useStoreState((state) => state.editorModel.isShowCollapse);
    const setIsShowCollapse = useStoreActions((actions) => actions.editorModel.setIsShowCollapse);

    const isActive = isShowCollapse && data.collapseType === currentType;

    const menuClickHandle = async (e: IMenuItem) => {
        if (lockApp.isLock && (e.collapseType !== CollapseType.MINE_MENU || !userInfo?.id)) return;
        if (disableApp.isDisable || isDisableAppAndShowPanel) return;
        // wasm lib 未初始化完成的时候, 不能切换水印面板, 否则水印工具会初始化失败
        if (!isWasmInt && e.collapseType === CollapseType.WATERMARK_MENU) return;

        if (e.collapseType === currentType) {
            setIsShowCollapse(!isShowCollapse);
            return;
        }
        // 检查是否需要确认
        const needConfirm = checkNeedConfirm(currentType);
        if (needConfirm) {
            // 触发确认事件
            events.emit(CLOSE_IMAGE_TRANS_EVENT, () => {
                // 确认后的回调
                handleMenuSwitch(e);
            });
            return;
        }
        // 直接切换菜单
        handleMenuSwitch(e);
    };

    // 抽取菜单切换逻辑
    const handleMenuSwitch = (e: IMenuItem) => {
        if (!isShowCollapse) {
            setIsShowCollapse(!isShowCollapse);
        }
        featureHelper.use(e.collapseType);
        setCurrentType(e.collapseType);
        setActualType(null);
        closeAiPPTPrompt();
        if (e.trackId) {
            window._stat_track('tpbj_subfunction_click', {
                func: e.trackId,
                open: true,
                record_id: getRecordInfo().id,
            });
        }
    };

    return (
        <div
            className={classNames(
                'p-menu-item',
                data.className,
                isActive ? 'active' : '',
                data.collapseType !== CollapseType.MINE_MENU || !userInfo?.id ? lockApp : '',
                disableApp.isDisable || isDisableAppAndShowPanel ? 'c-disable-app' : '',
            )}
            onClick={() => menuClickHandle(data)}
        >
            <div className="icon">
                <IconFont
                    name={isActive && data.activeIconName ? data.activeIconName : data.iconName}
                    fontSize={isIntl ? 20 : 24}
                />
            </div>

            <span className="text">{data.name}</span>
        </div>
    );
});

const Menu = () => {
    const featureHelper = useFeatureHelper();

    const setCurrentType = useStoreActions((actions) => actions.collapseModel.setCurrentType);
    const setBothType = useStoreActions((actions) => actions.collapseModel.setBothType);
    const getIsShowCollapse = useStoreActions((actions) => actions.editorModel.getIsShowCollapse);
    const getCurrentType = useStoreActions((actions) => actions.collapseModel.getCurrentType);
    const setIsShowCollapse = useStoreActions((actions) => actions.editorModel.setIsShowCollapse);
    const isWasmInt = useWasmIntState();

    const { menus } = useMenu();

    const scrollbars = useRef<Scrollbars>(null);

    useEffect(() => {
        const jump2Plugin = (data: OpenPluginType) => {
            featureHelper.use(CollapseType.MORE_MENU);
            setBothType(CollapseType.MORE_MENU);
            setTimeout(() => {
                events.emit(PLUGIN_OPEN_EVENT, data);
            }, 200);
        };

        events.on(MORE_COLLAPSE_EVENT, jump2Plugin);
        return () => {
            events.off(MORE_COLLAPSE_EVENT, jump2Plugin);
        };
    }, []);

    // 单画布，舞台页面快捷操作按钮点击事件
    useEffect(() => {
        const handler = editor.getInstance(QuickStageOperate).onQuickClick((_: string, isToggleMenu?: boolean) => {
            if (!isToggleMenu) return;

            const isShowCollapse = getIsShowCollapse();

            if (getCurrentType() === CollapseType.NAVIGATOR_MENU) {
                setIsShowCollapse(!isShowCollapse);
                return;
            }

            if (!isShowCollapse) {
                setIsShowCollapse(true);
            }

            featureHelper.use(CollapseType.NAVIGATOR_MENU);
            setCurrentType(CollapseType.NAVIGATOR_MENU);
        });
        return () => {
            editor.off(handler);
        };
    }, []);

    return (
        <React.Fragment>
            <Skeleton loading={false} backgroundColor="var(--background__main)" height="100%">
                <div className={classNames('p-menu')}>
                    <Scrollbars
                        ref={scrollbars}
                        onScrollFrame={() => {
                            events.emit(MENU_SCROLL_EVENT, scrollbars.current?.getScrollTop());
                        }}
                    >
                        <div className="p-menu-container">
                            {menus.map((v, i) => {
                                return !v.hidden ? <MenuItem key={i} data={v} isWasmInt={isWasmInt} /> : null;
                            })}
                        </div>
                    </Scrollbars>
                </div>
            </Skeleton>
        </React.Fragment>
    );
};

export default React.memo(Menu);
